import { Flex } from 'antd'
import React, { useCallback } from 'react'

import { AvatarType } from '@suntex.fim/enums'
import { LoaderOverlay } from '@suntex.fim/ui'

import AvatarPreview from '../../components/avatar/AvatarPreview'
import AvatarSection from '../../components/avatar/AvatarSection'
import ColorPicker from '../../components/avatar/ColorPicker'
import {
  AVATAR_COLORS,
  STICKER_REQUIREMENTS,
  animalAvatars,
  entertainmentAvatars,
  kidRow,
  musicAvatars,
  natureAvatars,
  skillAvatars,
  skillAvatarsLocked,
  specialAvatars,
  specialAvatarsLocked,
  sportsAvatars
} from '../../constants/avatarConstants'
import { useAvatar } from '../../hooks/useAvatar'

const MyAvatar: React.FC = () => {
  const {
    selectedColor,
    selectedAvatar,
    isLoading,
    currentLevel,
    currentStickerCount,
    handleColorSelect,
    handleAvatarSelect,
    saveAvatar
  } = useAvatar()

  const handleSave = useCallback(
    (type: AvatarType) => {
      saveAvatar(type)
    },
    [saveAvatar]
  )

  if (isLoading) {
    return <LoaderOverlay isLoading={isLoading} />
  }

  return (
    <>
      <Flex gap={24} vertical className="familylink">
        <h3 className="familylink__head">Create your player avatar!</h3>
      </Flex>
      <Flex gap={20} className="skill-avatar-container">
        <Flex vertical className="avatar-leftbox-container">
          <h4 className="fw-bold">Choose from Skill Avatars</h4>
          <Flex gap={15} vertical className="avatar-leftbox kid-avatar-box">
            {/* kids Icons */}
            <AvatarSection
              avatars={kidRow}
              title=""
              stickerRequirement={STICKER_REQUIREMENTS.DEFAULT}
              currentStickers={currentStickerCount}
              onAvatarClick={handleAvatarSelect}
            />
          </Flex>
          <h4 className="fw-bold">Choose from Skill Avatars</h4>
          <Flex gap={15} vertical className="avatar-leftbox">
            {/* Skill Level Icons */}
            <AvatarSection
              avatars={skillAvatars}
              lockedAvatars={skillAvatarsLocked}
              title="SKILL LEVEL ICONS: (earn level to unlock)"
              currentLevel={currentLevel}
              onAvatarClick={handleAvatarSelect}
              isSpecialSection={true}
            />

            {/* Special Sticker Avatars */}
            <AvatarSection
              avatars={specialAvatars}
              lockedAvatars={specialAvatarsLocked}
              title="Earn 10,000 stickers to unlock"
              currentLevel={currentLevel}
              currentStickers={currentStickerCount}
              onAvatarClick={handleAvatarSelect}
              isSpecialStickerSection={true}
            />

            {/* Sports Avatars */}
            <AvatarSection
              avatars={sportsAvatars}
              title={`Earn ${STICKER_REQUIREMENTS.SPORTS} stickers to unlock`}
              stickerRequirement={STICKER_REQUIREMENTS.SPORTS}
              currentStickers={currentStickerCount}
              onAvatarClick={handleAvatarSelect}
            />

            {/* Music Avatars */}
            <AvatarSection
              avatars={musicAvatars}
              title={`Earn ${STICKER_REQUIREMENTS.MUSIC} stickers to unlock`}
              stickerRequirement={STICKER_REQUIREMENTS.MUSIC}
              currentStickers={currentStickerCount}
              onAvatarClick={handleAvatarSelect}
            />

            {/* Animal Avatars */}
            <AvatarSection
              avatars={animalAvatars}
              title={`Earn ${STICKER_REQUIREMENTS.ANIMALS} stickers to unlock`}
              stickerRequirement={STICKER_REQUIREMENTS.ANIMALS}
              currentStickers={currentStickerCount}
              onAvatarClick={handleAvatarSelect}
            />

            {/* Nature Avatars */}
            <AvatarSection
              avatars={natureAvatars}
              title={`Earn ${STICKER_REQUIREMENTS.NATURE} stickers to unlock`}
              stickerRequirement={STICKER_REQUIREMENTS.NATURE}
              currentStickers={currentStickerCount}
              onAvatarClick={handleAvatarSelect}
            />

            {/* Entertainment Avatars */}
            <AvatarSection
              avatars={entertainmentAvatars}
              title={`Earn ${STICKER_REQUIREMENTS.ENTERTAINMENT} stickers to unlock`}
              stickerRequirement={STICKER_REQUIREMENTS.ENTERTAINMENT}
              currentStickers={currentStickerCount}
              onAvatarClick={handleAvatarSelect}
            />
          </Flex>
        </Flex>

        <Flex vertical align="center" className="avatar-rightbox">
          {/* Avatar Preview Section */}
          <AvatarPreview
            selectedAvatar={selectedAvatar}
            selectedColor={selectedColor}
            onSave={handleSave}
            isLoading={isLoading}
            currentLevel={currentLevel}
            currentStickers={currentStickerCount}
          />

          {/* Color Picker Section */}
          <ColorPicker
            colors={AVATAR_COLORS}
            selectedColor={selectedColor}
            onColorSelect={handleColorSelect}
          />
        </Flex>
      </Flex>
    </>
  )
}

export default MyAvatar
